import { bulkCloseContentRequested } from 'features/layout/features/content/actions';
import {
  selectContentCountByScope,
  selectIsLockedContentReadOnly,
  selectLockedContent,
} from 'features/layout/features/content/contentSlice';
import { useBulkCloseContent } from 'features/layout/grid-panel/useBulkCloseContent';
import { generateDontShowAgainKey } from 'features/layout/modals/BulkCloseContentConfirmationModal';
import { openModal } from 'features/modal/modalSlice';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getDontShowAgainPreference } from 'utils/dontShowAgain';
import { BulkCloseContentScope } from 'features/layout/types';
import { useCallback, useMemo } from 'react';

type ScopeConfig = {
  labelKey: string;
  permissionKey:
    'canManageAllContent' | 'canManageFiles' | 'canManageScreenshares' | 'canManageWhiteboards';
  lockedType?: 'all' | 'file' | 'screenshare' | 'whiteboard';
};

const SCOPE_CONFIG: Record<BulkCloseContentScope, ScopeConfig> = {
  all: {
    permissionKey: 'canManageAllContent',
    labelKey: 'tooltips.close_all_content',
    lockedType: 'all',
  },
  files: {
    permissionKey: 'canManageFiles',
    lockedType: 'file',
    labelKey: 'content_selector.close_all_files',
  },
  screenshares: {
    permissionKey: 'canManageScreenshares',
    lockedType: 'screenshare',
    labelKey: 'content_selector.close_all_screenshares',
  },
  whiteboards: {
    permissionKey: 'canManageWhiteboards',
    lockedType: 'whiteboard',
    labelKey: 'content_selector.close_all_whiteboards',
  },
};

export const useBulkCloseScopeControl = (scope: BulkCloseContentScope) => {
  const { t } = useTranslation('room');

  const dispatch = useAppDispatch();

  const config = SCOPE_CONFIG[scope];

  const itemCount = useAppSelector((state) => selectContentCountByScope(state, scope));

  const isLockedContentReadOnly = useAppSelector(selectIsLockedContentReadOnly);
  const lockedContent = useAppSelector(selectLockedContent);

  const permissions = useBulkCloseContent();
  const canManage = permissions[config.permissionKey];

  const canCloseLockContent = useMemo(() => {
    if (config.lockedType === 'all') {
      return !isLockedContentReadOnly;
    }

    return lockedContent?.type !== config.lockedType || !isLockedContentReadOnly;
  }, [lockedContent, config, isLockedContentReadOnly]);

  const enabled = canManage && itemCount > 1 && canCloseLockContent;

  const handleClick = useCallback(() => {
    const skipConfirmation = getDontShowAgainPreference(generateDontShowAgainKey(scope));

    if (skipConfirmation) {
      dispatch(bulkCloseContentRequested(scope));
    } else {
      dispatch(openModal('bulkCloseContentConfirmation', { scope }));
    }
  }, [dispatch, scope]);

  return {
    enabled,
    handleClick,
    label: t(config.labelKey),
  };
};
